import type { BoardVisit } from "@board/boardVisit";
import type { PlacedVisit } from "@board/VisitCard";
import { groupBy } from "@utils/group-by";

/** One board card: a visit, or a household's visits in the same lane, position and room. */
export interface BoardCardGroup<V extends BoardVisit = BoardVisit> {
  /** In column order; the first is the lead, whose time, delay and order the card takes. */
  visits: [PlacedVisit<V>, ...PlacedVisit<V>[]];
  /** The household's other pets on the board, in another position or room. */
  elsewhere: V[];
}

const cardKey = (placed: PlacedVisit): string =>
  `${placed.visit.householdId}|${placed.lane}|${placed.position}|${placed.visit.roomId ?? ""}`;

/**
 * Groups each column's placed visits (already in column order) by household
 * id, never name (not identity-safe); a card falls where its lead does.
 */
export const groupBoardVisits = <V extends BoardVisit>(
  columns: ReadonlyMap<string, readonly PlacedVisit<V>[]>,
): Map<string, BoardCardGroup<V>[]> => {
  const households = new Map(
    groupBy([...columns.values()].flat(), (p) => p.visit.householdId).map(
      (placed) => [placed[0].visit.householdId, placed.map((p) => p.visit)],
    ),
  );
  return new Map(
    [...columns].map(([key, column]) => [
      key,
      groupBy(column, cardKey).map((visits) => {
        const own = new Set(visits.map((p) => p.visit.id));
        return {
          visits,
          elsewhere: (households.get(visits[0].visit.householdId) ?? []).filter(
            (visit) => !own.has(visit.id),
          ),
        };
      }),
    ]),
  );
};

const andList = (names: readonly string[]): string =>
  names.length < 2
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;

/** "with Tofu", "with Tofu and Miso"; null when the household is all on this card. */
export const siblingMark = (
  elsewhere: readonly Pick<BoardVisit, "patientName">[],
): string | null =>
  elsewhere.length === 0
    ? null
    : `with ${andList(elsewhere.map((visit) => visit.patientName))}`;

/** The card's DVMs, each once, in card order. */
export const dvmNames = (names: readonly (string | null)[]): string[] =>
  [...new Set(names)].filter((name) => name !== null);
